/*
 * @Author: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @Date: 2025-01-17 20:00:00
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2026-03-04 21:37:45
 */
import React, { useState, useEffect, useRef, useMemo } from 'react'
import {
  Drawer,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  message,
  Modal,
  Alert
} from 'antd'
import {
  CloseOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import {
  setCreateRectifyDrawerVisible,
  closeRectifyDrawer,
  setFormData,
  clearFormData
} from '../../../slices/rectifySlice'
import {
  createRectification,
  fetchRemediationStatus,
  fetchRemediationSet
} from '../../../actions/rectify'
import { clearPhotoPickerPhotos } from '../../../actions/photoPicker'
import { updatePhotoRectification } from '../../../actions/photo'
import { setPaymentVisible } from '../../../actions/main'
import { setPhotoRectification } from '../../../slices/photosByContentSlice'
import parseQueryString from '../../../utils/parseQueryString'
import './index.scss'
import imgRectify from '../../../images/recitfy.png'
import sensors, { sensorsRole } from '../../../utils/sensors'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import AIDescriptionGenerator from './components/AIDescriptionGenerator'

const { TextArea } = Input
const { Option } = Select
const FormItem = Form.Item

interface CreateRectifyDrawerProps {
  photos?: any[]
  fromPlace?: string
  onSuccess?: () => void // 成功回调函数，用于刷新数据
}

/**
 * 发起整改侧边抽屉组件
 */
const CreateRectifyDrawer: React.FC<CreateRectifyDrawerProps> = (props) => {
  const { photos = [], onSuccess } = props
  const [form] = Form.useForm()

  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  // 从 Redux 获取状态
  const visible = useAppSelector(
    (state) => state.rectify?.createRectifyDrawerVisible || false
  )
  const presetPhotos = useAppSelector(
    (state) => state.rectify?.presetPhotos || []
  )
  const isPhotosLocked = useAppSelector(
    (state) => state.rectify?.isPhotosLocked || false
  )
  const members = useAppSelector((state) => state.group?.members || [])
  const groupId = useAppSelector((state) => state.group?.groupId)
  const myRole = useAppSelector((state) => state.group?.myRole)
  const industryCode = useAppSelector((state) => state.group?.industryCode)
  const userId = useAppSelector((state) => state.main?.user?.user_id)
  const selectedPhotos = useAppSelector(
    (state) => state.photoPicker?.photos || []
  )
  const backFromPickerPage = useAppSelector(
    (state) => state.photoPicker?.backFromPickerPage || false
  )
  const reduxFormData = useAppSelector(
    (state) =>
      state.rectify?.formData || {
        taskDescription: '',
        assignee: [],
        deadline: null,
        photos: []
      }
  )

  // 将 action creators 包装为 dispatch 调用
  const updateReduxFormData = (data: any) => dispatch(setFormData(data))
  const clearReduxFormData = () => dispatch(clearFormData())

  // 使用Redux中的表单数据
  const formData = reduxFormData

  // 添加会员状态相关状态
  const [remediationStatus, setRemediationStatus] = useState<{
    isVvip: boolean
    remediationTimes: number
  } | null>(null)

  const photosInitializedRef = useRef<boolean>(false)
  const backFromPickerProcessedRef = useRef<boolean>(false)
  const isUpdatingFromReduxRef = useRef<boolean>(false)
  const lastSyncedFormDataRef = useRef<any>(null)

  // 使用useMemo来稳定化数组引用，避免无限循环
  const memoizedPhotos = useMemo(() => photos, [photos?.length])
  const memoizedPresetPhotos = useMemo(
    () => presetPhotos,
    [presetPhotos?.length]
  )
  const memoizedSelectedPhotos = useMemo(
    () => selectedPhotos,
    [selectedPhotos?.length]
  )

  // 获取整改状态信息
  const fetchRemediationInfo = async () => {
    if (!groupId || !userId) return

    try {
      const result = await dispatch(
        fetchRemediationStatus({
          groupID: groupId,
          userID: userId
        })
      )

      if (result) {
        setRemediationStatus({
          isVvip: result.isVvip || false,
          remediationTimes: result.remediationTimes || 0
        })
      }
    } catch (error) {
      console.error('获取整改状态失败:', error)
    }
  }

  // 监听抽屉显示状态，触发曝光埋点和获取整改状态
  useEffect(() => {
    if (visible) {
      handleSensorOfExposureEvent()
      fetchRemediationInfo()
    } else {
      // 抽屉关闭时重置状态
      setRemediationStatus(null)
    }
  }, [visible, groupId, userId])

  // 处理从照片选择页面返回的逻辑
  useEffect(() => {
    if (
      backFromPickerPage &&
      memoizedSelectedPhotos.length > 0 &&
      !backFromPickerProcessedRef.current
    ) {
      // 检查URL中是否有整改抽屉标记
      const query = parseQueryString()
      if (query.rectifyDrawer === 'true') {
        // 恢复抽屉状态并设置选中的照片
        dispatch(setCreateRectifyDrawerVisible(true))
        updateReduxFormData({
          photos: memoizedSelectedPhotos
        })

        // 清理URL参数
        window.history.replaceState({}, '', window.location.pathname)

        // 重置photoPicker状态
        dispatch(clearPhotoPickerPhotos())

        // 标记已处理
        backFromPickerProcessedRef.current = true
      }
    }

    // 重置处理标记当不是从选择器返回时
    if (!backFromPickerPage) {
      backFromPickerProcessedRef.current = false
    }
  }, [backFromPickerPage, memoizedSelectedPhotos])

  // 当抽屉显示时处理初始化
  useEffect(() => {
    if (visible) {
      // 重置照片初始化标记
      photosInitializedRef.current = false
    } else {
      // 抽屉关闭时清空表单
      clearReduxFormData()
      photosInitializedRef.current = false
      lastSyncedFormDataRef.current = null
    }
  }, [visible, groupId])

  // 单独处理照片初始化，避免无限循环
  useEffect(() => {
    if (visible && !photosInitializedRef.current) {
      if (memoizedPresetPhotos.length > 0) {
        updateReduxFormData({
          photos: memoizedPresetPhotos
        })
        photosInitializedRef.current = true
      } else if (memoizedPhotos.length > 0) {
        updateReduxFormData({
          photos: memoizedPhotos
        })
        photosInitializedRef.current = true
      }
    }
  }, [visible, memoizedPresetPhotos.length, memoizedPhotos.length])

  // 同步Redux状态到表单字段
  useEffect(() => {
    if (visible && formData) {
      // 检查是否真的需要同步 - 对比上次同步的数据
      const currentData = {
        taskDescription: formData.taskDescription,
        assignee: formData.assignee,
        deadline: formData.deadline
      }

      const lastSyncedData = lastSyncedFormDataRef.current
      const needsSync =
        !lastSyncedData ||
        lastSyncedData.taskDescription !== currentData.taskDescription ||
        JSON.stringify(lastSyncedData.assignee) !==
          JSON.stringify(currentData.assignee) ||
        lastSyncedData.deadline !== currentData.deadline

      if (needsSync) {
        isUpdatingFromReduxRef.current = true
        form.setFieldsValue({
          taskDescription: formData.taskDescription,
          assignee: formData.assignee,
          deadline: formData.deadline ? dayjs(formData.deadline) : null
        })

        // 保存这次同步的数据
        lastSyncedFormDataRef.current = currentData

        // 延迟重置标志，确保onChange事件完成
        setTimeout(() => {
          isUpdatingFromReduxRef.current = false
        }, 0)
      }
    }
  }, [
    visible,
    formData.taskDescription,
    formData.assignee,
    formData.deadline,
    form
  ])

  const handleClose = (isSendSensor = true) => {
    if (isSendSensor) {
      // 上报取消埋点
      handleSensorOfClickEvent('取消')
    }

    form.resetFields()
    clearReduxFormData()
    dispatch(closeRectifyDrawer())
    // 重置同步标记
    lastSyncedFormDataRef.current = null
  }

  // 验证表单和数据
  const validateFormData = (values: any) => {
    // 检查是否至少有照片或任务描述
    if (
      !values.taskDescription &&
      (!formData.photos || formData.photos.length === 0)
    ) {
      message.error('请输入任务描述或添加照片')
      return false
    }

    // 检查必要的状态数据
    if (!groupId || !userId) {
      message.error('缺少必要的用户信息，请刷新页面重试')
      return false
    }

    return true
  }

  const getPhotoOriginalUrl = (photo: any) => {
    if (!photo) return ''

    if (typeof photo === 'string') {
      return photo.split('?')[0]
    }

    const candidate =
      photo.videoURL || photo.largeURL || photo.smallURL || photo.file || ''
    return candidate ? candidate.split('?')[0] : ''
  }

  const aiImageUrls = useMemo(() => {
    if (!formData?.photos || !Array.isArray(formData.photos)) return []
    return formData.photos.map(getPhotoOriginalUrl).filter(Boolean)
  }, [formData.photos])

  // 格式化照片URL
  const formatPhotoUrl = (photo: any) => {
    if (typeof photo === 'string') return photo

    if (photo.mediaType === 1) {
      return `${photo.videoURL.split('?')[0]}?w=${photo.videoInfo.width}&h=${
        photo.videoInfo.height
      }`
    }
    if (photo.imageSize) {
      return `${photo.largeURL.split('?')[0]}?w=${photo.imageSize.width}&h=${
        photo.imageSize.height
      }`
    } else if (photo.videoInfo) {
      return `${photo.largeURL.split('?')[0]}?w=${photo.videoInfo.width}&h=${
        photo.videoInfo.height
      }`
    }
    return photo.largeURL
  }

  // 构建API请求参数
  const buildApiData = (values: any) => ({
    deadline: values.deadline
      ? values.deadline.format('YYYY-MM-DD HH:mm:ss')
      : '',
    groupID: groupId,
    photoFilesBefore: (formData.photos || []).map(formatPhotoUrl),
    qrcodeList: (formData.photos || []).map((item) => {
      if (!item.extension) return ''

      let extensionObj: any
      try {
        if (typeof item.extension === 'string') {
          extensionObj = JSON.parse(item.extension)
        } else {
          extensionObj = item.extension
        }
      } catch {
        extensionObj = {}
      }

      return extensionObj?.rectification_qa_code || ''
    }),
    rectificationUserIDs: values.assignee || [],
    remark: values.taskDescription || '',
    userID: userId || ''
  })

  // 更新照片的整改状态
  const updatePhotosRectificationStatus = (recordID: number) => {
    ;(formData.photos || []).forEach((photo) => {
      const photoUrl = photo.videoURL || photo.largeURL || photo.smallURL
      if (photoUrl) {
        // 更新照片整改状态（新版和旧版都更新，后面移除旧版）
        dispatch(
          updatePhotoRectification({
            photoUrl: photoUrl.split('?')[0],
            photoRectification: { exist: true, recordID }
          })
        )
        dispatch(
          setPhotoRectification({
            photoUrl: photoUrl.split('?')[0],
            photoRectification: { exist: true, recordID }
          })
        )
      }
    })
  }

  // 处理引导弹窗逻辑
  const handleGuideModal = () => {
    const currentPath = window.location.pathname
    const isAllPhotoPage = currentPath === '/photo/all'
    const hasShownRectifyGuide = localStorage.getItem('hasShownRectifyGuide')

    if (isAllPhotoPage && !hasShownRectifyGuide) {
      localStorage.setItem('hasShownRectifyGuide', 'true')
      handleSensorOfGuideEvent('出现')
      showRectifyGuideModal()
    }
  }

  // 处理成功后的操作
  const handleSuccess = (result: any) => {
    message.success('发起整改成功')
    handleGuideModal()
    handleClose(false)

    // 调用成功回调
    if (onSuccess) {
      onSuccess()
    }

    // 派发自定义事件，通知整改列表页面刷新数据
    window.dispatchEvent(
      new CustomEvent('rectifyTaskCreated', { detail: { result } })
    )
  }

  // 处理错误
  const handleError = (error: any) => {
    console.error('发起整改失败:', error)
    message.error('发起整改失败，请重试')
  }

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        // 验证表单数据
        if (!validateFormData(values)) {
          return
        }

        // 构建API请求参数
        const apiData = buildApiData(values)

        // 上报发起埋点
        const deadline = values.deadline
          ? values.deadline.format('YYYY-MM-DD HH:mm:ss')
          : null
        // 获取默认照片的拍摄人ID（如果有预设照片的话）
        const photographerID =
          memoizedPresetPhotos.length > 0 && memoizedPresetPhotos[0].userID
            ? String(memoizedPresetPhotos[0].userID)
            : undefined

        // 调用创建整改任务的API
        dispatch(createRectification(apiData))
          .then((result) => {
            if (result && result.data.recordID) {
              // 上报发起成功埋点，包含任务编号
              handleSensorOfClickEvent('发起', {
                deadline,
                recordID: result.data.recordID,
                photographerID
              })

              // 更新相关照片的整改状态
              updatePhotosRectificationStatus(result.data.recordID)
              // 处理成功后的操作
              handleSuccess(result)
              // 调用接口减去整改免费发起条数
              dispatch(
                fetchRemediationSet({
                  groupID: groupId,
                  userID: userId
                })
              )
            }
          })
          .catch(handleError)
      })
      .catch(() => {
        // 表单验证失败，不做额外处理
      })
  }

  // 处理发起按钮点击（新增：包含验证提示）
  const handleSubmitClick = () => {
    // 如果没有免费条数打开vip开通弹窗
    if (
      remediationStatus &&
      !remediationStatus.isVvip &&
      remediationStatus.remediationTimes <= 0
    ) {
      handleOpenVip()
      return
    }
    // 检查是否满足提交条件，如果不满足则提示用户
    if (
      !formData.taskDescription.trim() &&
      (!formData.photos || formData.photos.length === 0)
    ) {
      message.warning('请输入任务描述或添加照片')
      return
    }

    // 满足条件则执行提交
    handleSubmit()
  }

  const handleAddPhotos = () => {
    if (isPhotosLocked) {
      message.info('从照片查看器进入的整改任务，无法修改照片')
      return
    }

    // 上报添加图片埋点
    handleSensorOfClickEvent('添加图片')

    // 设置sessionStorage标识，表示从整改抽屉跳转到照片选择页面
    sessionStorage.setItem('fromRectifyDrawer', 'true')

    // 在URL中添加标记，表示从整改抽屉跳转
    const currentUrl = window.location.pathname + window.location.search
    const separator = currentUrl.includes('?') ? '&' : '?'
    window.history.replaceState(
      {},
      '',
      `${currentUrl}${separator}rectifyDrawer=true`
    )

    // 跳转到照片选择页面
    const targetUrl = `/photo/picker?from=rectify&pageTitle=选择整改照片&mediaType=0&max=9`
    navigate(encodeURI(targetUrl))
  }

  // 显示整改引导弹窗
  const showRectifyGuideModal = () => {
    Modal.info({
      title: '发起整改成功，请前往整改记录查看',
      icon: null,
      content: (
        <div style={{ textAlign: 'center', paddingTop: '15px' }}>
          <img src={imgRectify} alt="整改记录" style={{ width: '277px' }} />
        </div>
      ),
      okText: '我知道了',
      onOk: () => {
        handleSensorOfGuideEvent('我知道了')
      },
      width: 420,
      centered: true,
      className: 'rectify-guide-modal'
    })
  }

  const handleRemovePhoto = (index: number) => {
    if (isPhotosLocked) {
      message.info('从照片查看器进入的整改任务，无法删除照片')
      return
    }

    // 上报删除图片埋点
    handleSensorOfClickEvent('删除图片')

    const newPhotos = [...formData.photos]
    newPhotos.splice(index, 1)
    updateReduxFormData({
      photos: newPhotos
    })
  }

  // 表单验证器
  const taskDescriptionValidator = (_rule: any, value: string) => {
    if (!value && !formData.photos?.length) {
      return Promise.reject('任务描述和照片至少填写一项')
    }
    return Promise.resolve()
  }

  const handleApplyAIDescription = (content: string) => {
    if (!content) return
    isUpdatingFromReduxRef.current = true
    form.setFieldsValue({ taskDescription: content })
    updateReduxFormData({ taskDescription: content })
    setTimeout(() => {
      isUpdatingFromReduxRef.current = false
    }, 0)
  }

  const handleTextAreaChange = (e: any) => {
    if (!isUpdatingFromReduxRef.current) {
      updateReduxFormData({ taskDescription: e.target.value })
    }
  }

  // 处理任务描述焦点事件
  const handleTextAreaFocus = () => {
    handleSensorOfClickEvent('编辑任务描述')
  }

  // 处理开通会员按钮点击
  const handleOpenVip = () => {
    // 关闭侧边抽屉
    dispatch(setCreateRectifyDrawerVisible(false))

    // 调用setPaymentVisible，参数为'web端整改任务条数限制'
    dispatch(setPaymentVisible(true, 'web端整改任务条数限制'))
  }

  // 渲染会员状态提示框
  const renderVipStatusAlert = () => {
    if (!remediationStatus || remediationStatus.isVvip) {
      return null
    }

    const { remediationTimes } = remediationStatus
    const isLowQuota = remediationTimes <= 0

    return (
      <Alert
        message={
          <div className="alert-content">
            <div className="alert-info">
              {isLowQuota ? (
                <ExclamationCircleOutlined
                  className={isLowQuota ? 'exclamation-icon' : 'info-icon'}
                />
              ) : (
                <InfoCircleOutlined
                  className={isLowQuota ? 'exclamation-icon' : 'info-icon'}
                />
              )}
              <span>整改免费发起条数剩余 {remediationTimes} 条</span>
            </div>
            <Button
              type="link"
              onClick={handleOpenVip}
              className={`open-vip-btn ${
                isLowQuota ? 'low-quota' : 'normal-quota'
              }`}
            >
              开通会员
            </Button>
          </div>
        }
        type={isLowQuota ? 'warning' : 'info'}
        showIcon={false}
        className={`vip-status-alert ${
          isLowQuota ? 'low-quota' : 'normal-quota'
        }`}
      />
    )
  }

  // 渲染单个照片项
  const renderPhotoItem = (photo: any, index: number) => {
    const isVideo = photo.mediaType === 1 || photo.type === 'video'

    return (
      <div key={index} className="photo-item">
        <div className="photo-wrapper">
          <img
            src={photo.smallURL || photo.file || photo}
            alt={`照片${index + 1}`}
            className="photo-thumbnail"
          />
          {isVideo && (
            <div className="video-indicator">
              <PlayCircleOutlined />
            </div>
          )}
        </div>
        {!isPhotosLocked && (
          <div className="remove-btn" onClick={() => handleRemovePhoto(index)}>
            <CloseOutlined />
          </div>
        )}
      </div>
    )
  }

  // 判断是否从照片预览页面发起
  const isFromPhotoPreview = window.location.pathname === '/photo/all'

  // 渲染添加照片按钮
  const renderAddPhotoButton = () => {
    if (isFromPhotoPreview || isPhotosLocked) {
      return null
    }

    return (
      <div className="add-photo-item" onClick={handleAddPhotos}>
        <div className="add-photo-content">
          <PlusOutlined />
          <span>重新选择</span>
        </div>
      </div>
    )
  }

  // 渲染照片选择区域
  const renderPhotoUploadArea = () => (
    <div className="photo-upload-area">
      <div className="photos-preview">
        {formData.photos.map(renderPhotoItem)}
        {renderAddPhotoButton()}
      </div>
    </div>
  )

  // 埋点相关代码
  const handleSensorOfGuideEvent = (clickItem) => {
    sensors.track('workgroup_b7603_completed', {
      clickItem,
      groupIndustry: industryCode,
      role: sensorsRole(myRole),
      groupID: groupId,
      track_sign: 'aworkgroup.b7603.completed.workgroup_b7603_completed',
      requirement_id: 156
    })
  }

  // 发起整改页面点击事件埋点
  const handleSensorOfClickEvent = (clickItem: string, options: any = {}) => {
    const baseParams: any = {
      clickItem,
      groupIndustry: industryCode,
      role: sensorsRole(myRole),
      groupID: groupId,
      fromPlace:
        window.location.pathname === '/rectify/list'
          ? '整改记录页'
          : '照片预览页',
      track_sign: 'aworkgroup.b7603.click.workgroup_b7603_click',
      requirement_id: 156
    }

    if (window.location.pathname === '/photo/all') {
      baseParams.string1 = formData.photos[0].userID
    }

    // 根据不同的clickItem添加特定参数
    if (clickItem === '发起') {
      // 发起时需要上报的所有参数
      const assigneeCount = formData.assignee ? formData.assignee.length : 0
      const photoCount = formData.photos ? formData.photos.length : 0
      const taskDescription = formData.taskDescription || ''

      baseParams.number1 = assigneeCount // 指定待办人的数量
      baseParams.photoNum = photoCount // 照片数量
      baseParams.inputContent = taskDescription // 任务描述内容

      // 如果有截止时间则上报
      if (options.deadline) {
        baseParams.datetime3 = options.deadline
      }

      // 如果有任务编号则上报
      if (options.recordID) {
        baseParams.number2 = options.recordID
      }

      // 如果有默认照片的拍摄人ID则上报
      if (options.photographerID) {
        baseParams.string1 = options.photographerID
      }
    } else if (clickItem === '指定代办人') {
      // 指定代办人时上报数量，优先使用传入的assigneeCount，避免Redux状态更新延迟问题
      const assigneeCount =
        options.assigneeCount !== undefined
          ? options.assigneeCount
          : formData.assignee
            ? formData.assignee.length
            : 0
      baseParams.number1 = assigneeCount
    }
    sensors.track('workgroup_b7603_click', baseParams)
  }

  // 发起整改页面曝光埋点
  const handleSensorOfExposureEvent = () => {
    // 检查是否是从照片选择页面返回，如果是则不上报曝光埋点
    const isFromPhotoPicker =
      sessionStorage.getItem('fromRectifyDrawer') === 'true'

    if (isFromPhotoPicker) {
      sessionStorage.removeItem('fromRectifyDrawer')
      return
    }

    sensors.track('workgroup_b7603_exposure', {
      fromPlace:
        window.location.pathname === '/rectify/list'
          ? '整改记录页'
          : '照片预览页',
      groupIndustry: industryCode,
      role: sensorsRole(myRole),
      groupID: groupId,
      track_sign: 'aworkgroup.b7603.exposure.workgroup_b7603_exposure',
      requirement_id: 156
    })
  }

  return (
    <Drawer
      title="发起整改"
      placement="right"
      size={600}
      open={visible}
      onClose={() => handleClose(true)}
      className="create-rectify-drawer"
    >
      <div className="drawer-content">
        <div className="drawer-body">
          {/* 添加会员状态提示框 */}
          {renderVipStatusAlert()}

          {visible && (
            <AIDescriptionGenerator
              imageUrls={aiImageUrls}
              onApply={handleApplyAIDescription}
              userId={userId}
            />
          )}

          <Form form={form} layout="vertical">
            <FormItem
              label={<span>任务描述</span>}
              name="taskDescription"
              rules={[{ validator: taskDescriptionValidator }]}
            >
              <TextArea
                placeholder="请输入文本"
                rows={4}
                maxLength={500}
                onChange={handleTextAreaChange}
                onFocus={handleTextAreaFocus}
              />
            </FormItem>

            <FormItem label="添加照片">{renderPhotoUploadArea()}</FormItem>

            <FormItem
              label={
                <span>
                  待办人（可多选）
                  <span className="note"> (非团队成员可微信完成)</span>
                </span>
              }
              name="assignee"
            >
              <Select
                mode="multiple"
                placeholder="请选择待办人（可多选）"
                showSearch
                filterOption={(input, option: any) => {
                  try {
                    // 安全获取 children 内容
                    const children = option.children
                    if (!children) return false

                    // 处理不同类型的 children
                    let text = ''
                    if (typeof children === 'string') {
                      text = children
                    } else if (typeof children === 'number') {
                      text = children.toString()
                    } else if (Array.isArray(children)) {
                      text = children.join('')
                    } else if (children && typeof children === 'object') {
                      // 如果是 React 元素，尝试获取其文本内容
                      text = children.props?.children || ''
                    }

                    return text.toLowerCase().includes(input.toLowerCase())
                  } catch (error) {
                    console.error('filterOption error:', error)
                    return false
                  }
                }}
                maxTagCount={3}
                maxTagTextLength={8}
                onChange={(value) => {
                  if (!isUpdatingFromReduxRef.current) {
                    const assigneeList = Array.isArray(value)
                      ? value.map((v) => String(v))
                      : [String(value)]
                    updateReduxFormData({ assignee: assigneeList })

                    // 上报指定代办人埋点，直接传入最新的assigneeList数量
                    handleSensorOfClickEvent('指定代办人', {
                      assigneeCount: assigneeList.length
                    })
                  }
                }}
              >
                {members
                  .filter((member: any) => {
                    // 过滤掉无效的成员数据
                    const hasValidId = member.user_id || member.id
                    const hasValidName = member.nickname || member.name
                    return hasValidId && hasValidName
                  })
                  .map((member: any) => {
                    const memberId = member.user_id || member.id
                    const memberName = member.nickname || member.name

                    return (
                      <Option key={memberId} value={memberId}>
                        {memberName}
                      </Option>
                    )
                  })}
              </Select>
            </FormItem>

            <FormItem
              label="截止时间"
              name="deadline"
              initialValue={dayjs().add(7, 'days')}
            >
              <DatePicker
                onChange={(date) => {
                  if (!isUpdatingFromReduxRef.current) {
                    updateReduxFormData({ deadline: date })

                    // 上报选择截止时间埋点
                    handleSensorOfClickEvent('选择截止时间')
                  }
                }}
                style={{ width: '100%' }}
                showTime={{ format: 'HH:mm' }}
                format="YYYY/MM/DD HH:mm"
                placeholder="请选择截止时间"
                disabledDate={(current) => {
                  // 不能选择今天之前的日期
                  return !!(current && current < dayjs().startOf('day'))
                }}
              />
            </FormItem>
          </Form>
        </div>

        <div className="drawer-footer">
          <Button onClick={() => handleClose(true)} style={{ marginRight: 8 }}>
            取消
          </Button>
          <Button
            type="primary"
            onClick={handleSubmitClick}
            className={
              remediationStatus &&
              !remediationStatus.isVvip &&
              remediationStatus.remediationTimes <= 0
                ? 'low-quota'
                : ''
            }
          >
            发起
          </Button>
        </div>
      </div>
    </Drawer>
  )
}

export default CreateRectifyDrawer
